<template>
  <div class="px-6 pt-8 relative">
    <div class="mb-6">
      <router-link class="breadcrumb-link" to="/publisher/plugins">
        Plugins
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-link" v-if="appDetail">{{ appDetail.name }}</span>
    </div>
    <div
      class="d-flex gap-3 align-center w-full flex-column flex-sm-row section-block mb-6"
    >
      <div class="d-flex flex-grow-1 max-w-full gap-3">
        <div class="PluginDetail__logo" :style="mainImageStyle"></div>
        <div class="flex-grow-1">
          <h3 class="PluginDetail__app-name" v-if="appDetail">
            {{ appDetail.name }}
          </h3>
          <div v-else class="PluginDetail__placeholder">
            <the-icon icon="document" class="mr-2" />
            Plugin name not available
          </div>
          <a
            v-if="appDetail && appDetail.url"
            class="d-inline-flex align-center PluginDetail__link"
            :href="appDetail.url"
            target="_blank"
          >
            <the-icon icon="link" class="" />
            <span>
              {{ appDetail.url }}
            </span>
          </a>
          <div v-else class="PluginDetail__placeholder">
            <the-icon icon="link" class="mr-2" />
            Plugin URL not provided
          </div>
        </div>
      </div>
      <div class="PluginDetail__status-audit">
        <StatusChip
          v-if="appDetail && appDetail.developerData"
          :status="appDetail.developerData.status"
          :dataId="String(appDetail.developerData.id)"
          @updateStatus="onUpdateStatus"
        />
        <div v-else class="PluginDetail__placeholder">
          <the-icon icon="settings" class="mr-2" />
          Status not available
        </div>
        <div class="PluginDetail__audit-wrapper">
          <v-tooltip
            v-for="dot in auditDots"
            :key="dot.id"
            top
            :open-delay="300"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                class="audit-chip"
                :class="getAuditDotClass(dot.status)"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <span>{{ dot.tooltip }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <!-- Content Section - Always shown -->
    <div class="w-full section-block mb-6">
      <v-row>
        <v-col cols="12" md="7">
          <h6 class="PluginDetail__section-heading">Content</h6>
          
          <!-- Short name (read-only) -->
          <div class="d-flex mb-4">
            <span class="PluginDetail__label">Short name</span>
            <span v-if="appDetail && appDetail.developerContent && appDetail.developerContent.shortName" class="text-sm font-bold block tracking-wide text-gray-600">
              {{ appDetail.developerContent.shortName }}
            </span>
            <span v-else class="PluginDetail__placeholder">
              <the-icon icon="edit" class="mr-2" />
              Short name required for plugin identification
            </span>
          </div>

          <!-- Description (editable) -->
          <div class="d-flex mb-4 align-start PluginDetail__editable-row">
            <span class="PluginDetail__label">Description</span>
            <div class="flex-grow-1">
              <div v-if="editingField !== 'description'">
                <div v-if="appDetail && appDetail.developerContent && appDetail.developerContent.description" class="text-sm block tracking-wide text-gray-600">
                  {{ appDetail.developerContent.description }}
                </div>
            <span v-else class="PluginDetail__placeholder">
              <the-icon icon="edit" class="mr-2" />
              Description required to explain plugin functionality
            </span>
                <v-btn v-if="isOwned" small text @click="startEdit('description')" class="mt-1 PluginDetail__edit-btn">
                  <v-icon small>mdi-pencil</v-icon> Edit
                </v-btn>
          </div>
              <div v-else class="d-flex flex-column">
                <v-textarea
                  v-model="editValue"
                  rows="3"
                  outlined
                  dense
                  class="mb-2"
                ></v-textarea>
                <div class="d-flex gap-2">
                  <v-btn small color="primary" @click="saveField('description')" :loading="loading">Save</v-btn>
                  <v-btn small text @click="cancelEdit">Cancel</v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Long Description (editable) -->
          <div class="d-flex mb-4 align-start PluginDetail__editable-row">
            <span class="PluginDetail__label">Long Description</span>
            <div class="flex-grow-1">
              <div v-if="editingField !== 'longDescription'">
                <div v-if="appDetail && appDetail.developerContent && appDetail.developerContent.longDescription" class="text-sm block tracking-wide text-gray-600" v-html="appDetail.developerContent.longDescription"></div>
                <span v-else class="PluginDetail__placeholder">
                  <the-icon icon="edit" class="mr-2" />
                  Long description not provided
                </span>
                <v-btn v-if="isOwned" small text @click="startEdit('longDescription')" class="mt-1 PluginDetail__edit-btn">
                  <v-icon small>mdi-pencil</v-icon> Edit
                </v-btn>
              </div>
              <div v-else class="d-flex flex-column">
                <v-textarea
                  v-model="editValue"
                  rows="5"
                  outlined
                  dense
                  class="mb-2"
                ></v-textarea>
                <div class="d-flex gap-2">
                  <v-btn small color="primary" @click="saveField('longDescription')" :loading="loading">Save</v-btn>
                  <v-btn small text @click="cancelEdit">Cancel</v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Website (editable) -->
          <div class="d-flex mb-4 PluginDetail__editable-row">
            <span class="PluginDetail__label">Website</span>
            <div class="flex-grow-1">
              <div v-if="editingField !== 'website'">
                <a v-if="appDetail && appDetail.developerContent && appDetail.developerContent.website" 
                   :href="appDetail.developerContent.website" 
                   target="_blank"
                   class="text-sm block tracking-wide text-blue-500">
                  {{ appDetail.developerContent.website }}
                </a>
                <span v-else class="PluginDetail__placeholder">
                  <the-icon icon="link" class="mr-2" />
                  Website URL not provided
                </span>
                <v-btn v-if="isOwned" small text @click="startEdit('website')" class="mt-1 PluginDetail__edit-btn">
                  <v-icon small>mdi-pencil</v-icon> Edit
                </v-btn>
              </div>
              <div v-else class="d-flex flex-column">
                <v-text-field v-model="editValue" outlined dense class="mb-2"></v-text-field>
                <div class="d-flex gap-2">
                  <v-btn small color="primary" @click="saveField('website')" :loading="loading">Save</v-btn>
                  <v-btn small text @click="cancelEdit">Cancel</v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Support Email (editable) -->
          <div class="d-flex mb-4 PluginDetail__editable-row">
            <span class="PluginDetail__label">Support Email</span>
            <div class="flex-grow-1">
              <div v-if="editingField !== 'supportEmail'">
                <a v-if="appDetail && appDetail.developerContent && appDetail.developerContent.supportEmail" 
                   :href="`mailto:${appDetail.developerContent.supportEmail}`" 
                   class="text-sm block tracking-wide text-blue-500">
                  {{ appDetail.developerContent.supportEmail }}
                </a>
                <span v-else class="PluginDetail__placeholder">
                  <the-icon icon="email" class="mr-2" />
                  Support email not provided
                </span>
                <v-btn v-if="isOwned" small text @click="startEdit('supportEmail')" class="mt-1 PluginDetail__edit-btn">
                  <v-icon small>mdi-pencil</v-icon> Edit
                </v-btn>
              </div>
              <div v-else class="d-flex flex-column">
                <v-text-field v-model="editValue" type="email" outlined dense class="mb-2"></v-text-field>
                <div class="d-flex gap-2">
                  <v-btn small color="primary" @click="saveField('supportEmail')" :loading="loading">Save</v-btn>
                  <v-btn small text @click="cancelEdit">Cancel</v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Documentation URL (editable) -->
          <div class="d-flex mb-4 PluginDetail__editable-row">
            <span class="PluginDetail__label">Documentation URL</span>
            <div class="flex-grow-1">
              <div v-if="editingField !== 'documentationUrl'">
                <a v-if="appDetail && appDetail.developerContent && appDetail.developerContent.documentationUrl" 
                   :href="appDetail.developerContent.documentationUrl" 
                   target="_blank"
                   class="text-sm block tracking-wide text-blue-500">
                  {{ appDetail.developerContent.documentationUrl }}
                </a>
                <span v-else class="PluginDetail__placeholder">
                  <the-icon icon="link" class="mr-2" />
                  Documentation URL not provided
                </span>
                <v-btn v-if="isOwned" small text @click="startEdit('documentationUrl')" class="mt-1 PluginDetail__edit-btn">
                  <v-icon small>mdi-pencil</v-icon> Edit
                </v-btn>
              </div>
              <div v-else class="d-flex flex-column">
                <v-text-field v-model="editValue" outlined dense class="mb-2"></v-text-field>
                <div class="d-flex gap-2">
                  <v-btn small color="primary" @click="saveField('documentationUrl')" :loading="loading">Save</v-btn>
                  <v-btn small text @click="cancelEdit">Cancel</v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Categories (editable) -->
          <div class="d-flex mb-4 align-start PluginDetail__editable-row">
            <span class="PluginDetail__label">Categories</span>
            <div class="flex-grow-1">
              <div v-if="editingField !== 'categories'">
                <div v-if="appDetail && appDetail.developerContent && appDetail.developerContent.categories && appDetail.developerContent.categories.length > 0" class="d-flex flex-wrap gap-2 mb-2">
                  <v-chip
                    v-for="(category, index) in appDetail.developerContent.categories"
                    :key="index"
                    small
                    color="primary"
                    outlined
                  >
                    {{ category }}
                  </v-chip>
                </div>
                <span v-else class="PluginDetail__placeholder">
                  <the-icon icon="tag" class="mr-2" />
                  Categories not set
                </span>
                <v-btn v-if="isOwned" small text @click="startEdit('categories')" class="mt-1 PluginDetail__edit-btn">
                  <v-icon small>mdi-pencil</v-icon> Edit
                </v-btn>
              </div>
              <div v-else class="d-flex flex-column">
                <v-select
                  v-model="editArray"
                  :items="predefinedCategories"
                  multiple
                  chips
                  deletable-chips
                  outlined
                  dense
                  class="mb-2"
                  placeholder="Select categories"
                ></v-select>
                <div class="d-flex gap-2">
                  <v-btn small color="primary" @click="saveField('categories')" :loading="loading">Save</v-btn>
                  <v-btn small text @click="cancelEdit">Cancel</v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Tags (editable) -->
          <div class="d-flex mb-4 align-start PluginDetail__editable-row">
            <span class="PluginDetail__label">Tags</span>
            <div class="flex-grow-1">
              <div v-if="editingField !== 'tags'">
                <div v-if="appDetail && appDetail.developerContent && appDetail.developerContent.tags && appDetail.developerContent.tags.length > 0" class="d-flex flex-wrap gap-2 mb-2">
                  <v-chip
                    v-for="(tag, index) in appDetail.developerContent.tags"
                    :key="index"
                    small
                    color="secondary"
                    outlined
                  >
                    {{ tag }}
                  </v-chip>
                </div>
                <span v-else class="PluginDetail__placeholder">
                  <the-icon icon="tag" class="mr-2" />
                  Tags not set
                </span>
                <v-btn v-if="isOwned" small text @click="startEdit('tags')" class="mt-1 PluginDetail__edit-btn">
                  <v-icon small>mdi-pencil</v-icon> Edit
                </v-btn>
              </div>
              <div v-else class="d-flex flex-column">
                <v-autocomplete
                  v-model="editArray"
                  :items="predefinedTags"
                  multiple
                  chips
                  deletable-chips
                  outlined
                  dense
                  class="mb-2"
                  placeholder="Select or type tags"
                  :search-input.sync="tagSearchInput"
                  @keyup.enter="addCustomTag"
                ></v-autocomplete>
                <div class="d-flex gap-2">
                  <v-btn small color="primary" @click="saveField('tags')" :loading="loading">Save</v-btn>
                  <v-btn small text @click="cancelEdit">Cancel</v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Privacy URL -->
          <div class="d-flex mb-4">
            <span class="PluginDetail__label">Privacy URL</span>
            <span v-if="appDetail && appDetail.developerContent && appDetail.developerContent.privacyURL" class="text-sm block tracking-wide text-gray-600">
              {{ appDetail.developerContent.privacyURL }}
            </span>
            <span v-else class="PluginDetail__placeholder">
              <the-icon icon="policy" class="mr-2" />
              Privacy policy URL required for compliance
            </span>
          </div>

          <!-- Terms URL -->
          <div class="d-flex mb-4">
            <span class="PluginDetail__label">Terms URL</span>
            <span v-if="appDetail && appDetail.developerContent && appDetail.developerContent.termsURL" class="text-sm block tracking-wide text-gray-600">
              {{ appDetail.developerContent.termsURL }}
            </span>
            <span v-else class="PluginDetail__placeholder">
              <the-icon icon="policy" class="mr-2" />
              Terms of service URL required for compliance
            </span>
          </div>

          <!-- Dev Mode -->
          <div class="d-flex mb-4">
            <span class="PluginDetail__label">Dev Mode</span>
            <span v-if="appDetail" class="text-sm block tracking-wide text-gray-600">
              {{ appDetail.devMode ? 'Enabled' : 'Disabled' }}
            </span>
            <span v-else class="PluginDetail__placeholder">
              <the-icon icon="settings" class="mr-2" />
              Development mode status not available
            </span>
          </div>

          <!-- Dev URL -->
          <div class="d-flex mb-4">
            <span class="PluginDetail__label">Dev URL</span>
            <span v-if="appDetail && appDetail.devURL" class="text-sm block tracking-wide text-gray-600">
              {{ appDetail.devURL }}
            </span>
            <span v-else class="PluginDetail__placeholder">
              <the-icon icon="link" class="mr-2" />
              Development URL not configured
            </span>
          </div>

          <!-- Forge Site API Key (Admin Only) -->
          <div v-if="isAdmin" class="d-flex mb-4 PluginDetail__editable-row">
            <span class="PluginDetail__label">Forge Site API Key</span>
            <div class="flex-grow-1">
              <div v-if="editingField !== 'forgeSiteApiKey'">
                <div class="d-flex align-center mb-2">
                  <span v-if="appDetail && appDetail.forge_api_configured" class="text-sm block tracking-wide text-gray-600">
                    <v-chip x-small color="success" dark class="mr-2">
                      Configured
                    </v-chip>
                    API key is set (hidden for security)
                  </span>
                  <span v-else class="PluginDetail__placeholder">
                    <the-icon icon="key" class="mr-2" />
                    Forge site API key not configured
                  </span>
                </div>
                <v-btn small text @click="startEdit('forgeSiteApiKey')" class="mt-1 PluginDetail__edit-btn">
                  <v-icon small>mdi-pencil</v-icon> {{ appDetail && appDetail.forge_api_configured ? 'Update' : 'Set' }} API Key
                </v-btn>
              </div>
              <div v-else class="d-flex flex-column">
                <v-text-field
                  v-model="editValue"
                  type="password"
                  outlined
                  dense
                  class="mb-2"
                  placeholder="Enter Forge site API key"
                  :append-icon="showApiKey ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showApiKey = !showApiKey"
                  :type="showApiKey ? 'text' : 'password'"
                ></v-text-field>
                <div class="d-flex gap-2">
                  <v-btn small color="primary" @click="saveField('forgeSiteApiKey')" :loading="loading">Save</v-btn>
                  <v-btn small text @click="cancelEdit">Cancel</v-btn>
                </div>
              </div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="5">
          <h6 class="PluginDetail__section-heading">Assets</h6>
          
          <!-- Icon Upload -->
          <div class="mb-4">
            <div class="d-flex align-center mb-2">
              <span class="PluginDetail__label">Icon</span>
              <v-btn v-if="isOwned" small text @click="$refs.iconInput.click()" :disabled="uploadingAsset === 'icon'">
                <v-icon small>mdi-upload</v-icon> Upload
              </v-btn>
              <input ref="iconInput" type="file" accept="image/png,image/jpeg,image/svg+xml" @change="handleIconUpload" style="display: none" />
            </div>
            <div v-if="appDetail && appDetail.developerContent && appDetail.developerContent.iconUrl" class="mb-2">
              <img :src="appDetail.developerContent.iconUrl" alt="Plugin icon" style="max-width: 64px; max-height: 64px;" />
            </div>
            <div v-else class="PluginDetail__placeholder mb-2">
              <the-icon icon="image" class="mr-2" />
              Icon not uploaded
            </div>
            <div v-if="assetGuidelines && assetGuidelines.icon" class="text-xs text-gray-500">
              Max: {{ assetGuidelines.icon.max_file_size_mb }}MB, Recommended: {{ assetGuidelines.icon.dimensions.recommended_width }}x{{ assetGuidelines.icon.dimensions.recommended_height }}px
            </div>
          </div>

          <!-- Thumbnail Upload -->
          <div class="mb-4">
            <div class="d-flex align-center mb-2">
              <span class="PluginDetail__label">Thumbnail</span>
              <v-btn v-if="isOwned" small text @click="$refs.thumbnailInput.click()" :disabled="uploadingAsset === 'thumbnail'">
                <v-icon small>mdi-upload</v-icon> Upload
              </v-btn>
              <input ref="thumbnailInput" type="file" accept="image/png,image/jpeg" @change="handleThumbnailUpload" style="display: none" />
            </div>
            <div v-if="appDetail && appDetail.developerContent && appDetail.developerContent.thumbnailUrl" class="mb-2">
              <img :src="appDetail.developerContent.thumbnailUrl" alt="Plugin thumbnail" style="max-width: 200px; max-height: 100px; object-fit: cover;" />
            </div>
            <div v-else class="PluginDetail__placeholder mb-2">
              <the-icon icon="image" class="mr-2" />
              Thumbnail not uploaded
            </div>
            <div v-if="assetGuidelines && assetGuidelines.thumbnail" class="text-xs text-gray-500">
              Max: {{ assetGuidelines.thumbnail.max_file_size_mb }}MB, Recommended: {{ assetGuidelines.thumbnail.dimensions.recommended_width }}x{{ assetGuidelines.thumbnail.dimensions.recommended_height }}px
            </div>
          </div>

          <!-- Screenshots -->
          <div>
            <div class="d-flex align-center mb-2">
              <span class="PluginDetail__label">Screenshots</span>
              <v-btn v-if="isOwned" small text @click="$refs.screenshotsInput.click()" :disabled="uploadingAsset === 'screenshots'">
                <v-icon small>mdi-upload</v-icon> Upload
              </v-btn>
              <input ref="screenshotsInput" type="file" accept="image/png,image/jpeg" multiple @change="handleScreenshotsUpload" style="display: none" />
            </div>
          <div v-if="appDetail && appDetail.developerContent && appDetail.developerContent.screenshots && appDetail.developerContent.screenshots.length > 0">
            <viewer
              :images="appDetail.developerContent.screenshots"
              :options="options"
              class="viewer"
            >
              <template #default="scope">
                <v-row>
                  <v-col
                    cols="4"
                      v-for="(image, index) in scope.images"
                    :key="image"
                    class="image-wrapper"
                  >
                      <div class="position-relative">
                    <img class="PluginDetail__screenshot" :src="image" />
                        <v-btn
                          v-if="isOwned"
                          icon
                          small
                          color="error"
                          class="position-absolute"
                          style="top: 4px; right: 4px;"
                          @click="handleDeleteScreenshot(index)"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                  </v-col>
                </v-row>
              </template>
            </viewer>
          </div>
          <div v-else class="PluginDetail__placeholder">
            <the-icon icon="upload" class="mr-2" />
            Screenshots required to showcase plugin functionality
            </div>
            <div v-if="assetGuidelines && assetGuidelines.screenshots" class="text-xs text-gray-500 mt-2">
              Max: {{ assetGuidelines.screenshots.max_file_size_mb }}MB per file, Recommended: {{ assetGuidelines.screenshots.dimensions.recommended_width }}x{{ assetGuidelines.screenshots.dimensions.recommended_height }}px, Max {{ assetGuidelines.screenshots.max_count }} screenshots
            </div>
          </div>

          <!-- Upload Errors -->
          <div v-if="uploadErrors.length > 0" class="mt-4">
            <v-alert type="error" dense>
              <div v-for="(error, index) in uploadErrors" :key="index">{{ error }}</div>
            </v-alert>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-row>
      <v-col>
        <!-- Developer Information Section - Always shown -->
        <div class="section-block mb-6">
          <h6 class="PluginDetail__section-heading">Developer Information</h6>
          <div v-if="appDetail && appDetail.developer">
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Name</span>
              <span class="text-sm block tracking-wide text-gray-600">
                <router-link
                  :to="{
                    name: 'developer_detail',
                    params: { developerId: appDetail.developer.id },
                  }"
                >
                  {{ appDetail.developer.name }}
                </router-link>
              </span>
            </div>
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Company</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.developer.company }}
              </span>
            </div>
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Website</span>
              <a
                v-if="appDetail.developer.website || (appDetail.developerContent && appDetail.developerContent.website)"
                :href="appDetail.developer.website || appDetail.developerContent.website"
                target="_blank"
                class="text-sm block tracking-wide text-blue-500 cursor-poionter"
              >
                {{ appDetail.developer.website || appDetail.developerContent.website }}
              </a>
              <span v-else class="PluginDetail__placeholder">
                Website not provided
              </span>
            </div>
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Contact Email</span>
              <a
                v-if="appDetail.developer.email || (appDetail.developerContent && appDetail.developerContent.supportEmail)"
                :href="`mailto:${appDetail.developer.email || appDetail.developerContent.supportEmail}`"
                class="text-sm block tracking-wide text-blue-500 cursor-poionter"
              >
                {{ appDetail.developer.email || appDetail.developerContent.supportEmail }}
              </a>
              <span v-else class="PluginDetail__placeholder">
                Contact email not provided
              </span>
            </div>
          </div>
          <div v-else class="PluginDetail__placeholder">
            <the-icon icon="developers" class="mr-2" />
            Developer information not available - please ensure developer profile is complete
          </div>
        </div>

        <!-- Plugin Audit Section - Always shown -->
        <div class="section-block mb-6">
          <div class="block w-full overflow-x-auto p-5">
            <h6 class="PluginDetail__section-heading">Plugin Audit</h6>

            <!-- Versions List -->
            <div class="mb-6">
              <h6 class="PluginDetail__subsection-heading">Versions</h6>
              <div v-if="pluginVersions && pluginVersions.length > 0">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Version</th>
                        <th class="text-left">Status</th>
                        <th class="text-left">Risk Level</th>
                        <th class="text-left">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="version in pluginVersions" :key="version.id">
                        <td>{{ version.version }}</td>
                        <td>
                          <v-chip x-small :color="getVersionStatusColor(version.status)" dark>
                            {{ version.status }}
                          </v-chip>
                        </td>
                        <td>
                          <v-chip v-if="version.risk_level" x-small :color="getRiskLevelColor(version.risk_level)" dark>
                            {{ version.risk_level }}
                          </v-chip>
                          <span v-else class="text-gray-500">-</span>
                        </td>
                        <td>{{ formatDate(version.created_at) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
              <div v-else-if="loadingAudit" class="PluginDetail__placeholder">
                <the-icon icon="loading" class="mr-2" />
                Loading versions...
              </div>
              <div v-else class="PluginDetail__placeholder">
                <the-icon icon="warn" class="mr-2" />
                No versions found
              </div>
            </div>

            <!-- Latest Analysis Report -->
            <div class="mb-6" v-if="latestAnalysisReport || pluginVersions.length > 0">
              <div class="d-flex justify-space-between align-center mb-4">
                <h6 class="PluginDetail__subsection-heading mb-0">Latest Analysis Report</h6>
                <v-btn
                  v-if="pluginVersions.length > 0 && !refreshingAnalysis"
                  small
                  color="primary"
                  outlined
                  @click="refreshAnalysisReport"
                  :disabled="refreshingAnalysis"
                >
                  <v-icon small left>mdi-refresh</v-icon>
                  Refresh Analysis
                </v-btn>
                <v-btn
                  v-else-if="refreshingAnalysis"
                  small
                  color="primary"
                  outlined
                  disabled
                >
                  <v-icon small left class="rotating">mdi-loading</v-icon>
                  Refreshing...
                </v-btn>
              </div>
              <div v-if="latestAnalysisReport">
                <div class="d-flex mb-4">
                  <span class="PluginDetail__label">Generated</span>
                  <span class="text-sm block text-gray-600">
                    {{ formatDate(latestAnalysisReport.generated_at) }}
                  </span>
                </div>
              <div class="d-flex mb-4">
                <span class="PluginDetail__label">Eval Safe</span>
                <span class="text-sm block text-gray-600 flex align-center">
                  <span
                    class="audit-chip d-inline-block"
                    :class="`${colors.evalSafe} mr-1`"
                  ></span>
                  {{ latestAnalysisReport.summary?.eval_safe ?? 'N/A' }}
                </span>
              </div>
              <div class="d-flex mb-4">
                <span class="PluginDetail__label">External Network Requests</span>
                <span class="text-sm block text-gray-600 flex align-center">
                  <span :class="`${colors.externalNetworkRequests} audit-chip mr-1`"></span>
                  {{ latestAnalysisReport.summary?.external_network_requests_count ?? 0 }}
                </span>
              </div>
              <div class="d-flex mb-4">
                <span class="PluginDetail__label">Secure Network URLs</span>
                <span class="text-sm block text-gray-600">
                  {{ latestAnalysisReport.summary?.secure_network_urls_count ?? 0 }}
                </span>
              </div>
              <div class="d-flex mb-4">
                <span class="PluginDetail__label">Insecure Network URLs</span>
                <span class="text-sm block text-gray-600">
                  {{ latestAnalysisReport.summary?.insecure_network_urls_count ?? 0 }}
                </span>
              </div>
              <div class="d-flex mb-4" v-if="latestAnalysisReport.details && latestAnalysisReport.details.analysis_result">
                <span class="PluginDetail__label">Network Request URLs</span>
                <span class="text-sm block tracking-wide text-gray-600">
                  <ul
                    v-if="latestAnalysisReport.details.analysis_result.ExternalNetworkRequests?.secureNetworkURLs?.length"
                    class="pl-0"
                  >
                    <li
                      v-for="(secureURL, i) in latestAnalysisReport.details.analysis_result.ExternalNetworkRequests.secureNetworkURLs"
                      :key="i"
                      class="d-flex align-center mb-1"
                    >
                      <span :class="`${colors.secureURL} audit-chip mr-1`"></span>
                      {{ secureURL }}
                    </li>
                  </ul>
                  <ul
                    v-if="latestAnalysisReport.details.analysis_result.ExternalNetworkRequests?.insecureNetworkURLs?.length"
                    class="pl-0"
                  >
                    <li
                      v-for="(insecureURL, i) in latestAnalysisReport.details.analysis_result.ExternalNetworkRequests.insecureNetworkURLs"
                      :key="i"
                      class="d-flex align-center mb-1"
                    >
                      <span :class="`${colors.insecureURL} audit-chip mr-1`"></span>
                      {{ insecureURL }}
                    </li>
                  </ul>
                  <span v-if="!latestAnalysisReport.details.analysis_result.ExternalNetworkRequests?.secureNetworkURLs?.length && !latestAnalysisReport.details.analysis_result.ExternalNetworkRequests?.insecureNetworkURLs?.length" class="text-gray-500">
                    No network requests
                </span>
                </span>
              </div>
              </div>
              <div v-else-if="loadingAudit" class="PluginDetail__placeholder">
                <the-icon icon="loading" class="mr-2" />
                Loading analysis report...
            </div>
                      <div v-else class="PluginDetail__placeholder">
            <the-icon icon="warn" class="mr-2" />
                Analysis report not available. Click "Refresh Analysis" to generate one.
              </div>
          </div>
            
            <!-- Policy Evaluations -->
            <div class="mb-6" v-if="pluginVersions.length > 0">
              <div class="d-flex justify-space-between align-center mb-4">
                <h6 class="PluginDetail__subsection-heading mb-0">Policy Evaluations</h6>
                <v-btn
                  v-if="!evaluatingPolicies"
                  small
                  color="primary"
                  outlined
                  @click="triggerPolicyEvaluation"
                  :disabled="evaluatingPolicies || pluginVersions.length === 0"
                >
                  <v-icon small left>mdi-shield-check</v-icon>
                  Evaluate Policies
                </v-btn>
                <v-btn
                  v-else
                  small
                  color="primary"
                  outlined
                  disabled
                >
                  <v-icon small left class="rotating">mdi-loading</v-icon>
                  Evaluating...
                </v-btn>
              </div>
              <div v-if="policyEvaluations && policyEvaluations.evaluations && policyEvaluations.evaluations.length > 0">
                <!-- Summary (if available from latest endpoint) -->
                <div v-if="policyEvaluations.summary" class="mb-4">
                  <div class="d-flex flex-wrap gap-3">
                    <div>
                      <span class="text-xs text-gray-500">Total Policy Sets:</span>
                      <span class="text-sm font-bold ml-1">{{ policyEvaluations.summary.total_policy_sets }}</span>
                    </div>
                    <div>
                      <span class="text-xs text-gray-500">Pass:</span>
                      <v-chip x-small color="success" dark class="ml-1">{{ policyEvaluations.summary.pass }}</v-chip>
                    </div>
                    <div>
                      <span class="text-xs text-gray-500">Warn:</span>
                      <v-chip x-small color="warning" dark class="ml-1">{{ policyEvaluations.summary.warn }}</v-chip>
                    </div>
                    <div>
                      <span class="text-xs text-gray-500">Fail:</span>
                      <v-chip x-small color="error" dark class="ml-1">{{ policyEvaluations.summary.fail }}</v-chip>
                    </div>
                    <div>
                      <span class="text-xs text-gray-500">Overall Status:</span>
                      <v-chip x-small :color="getEvaluationResultColor(policyEvaluations.summary.overall_status)" dark class="ml-1">
                        {{ policyEvaluations.summary.overall_status }}
                      </v-chip>
                    </div>
                  </div>
                </div>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Policy Set</th>
                        <th class="text-left">Result</th>
                        <th class="text-left">Reasons</th>
                        <th class="text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="evaluation in policyEvaluations.evaluations" :key="evaluation.id">
                        <td>
                          <span v-if="evaluation.policy_set_name">{{ evaluation.policy_set_name }}</span>
                          <span v-else>Policy Set #{{ evaluation.policy_set_id }}</span>
                        </td>
                        <td>
                          <v-chip x-small :color="getEvaluationResultColor(evaluation.result)" dark>
                            {{ evaluation.result }}
                          </v-chip>
                        </td>
                        <td>
                          <ul v-if="evaluation.reasons && evaluation.reasons.length > 0" class="pl-3 mb-0">
                            <li v-for="(reason, i) in evaluation.reasons" :key="i" class="text-xs">
                              {{ reason }}
                            </li>
                          </ul>
                          <span v-else class="text-gray-500 text-xs">-</span>
                        </td>
                        <td>
                          <div v-if="evaluation.recommended_actions && evaluation.recommended_actions.length > 0" class="d-flex flex-wrap gap-1">
                            <v-chip v-for="(action, i) in evaluation.recommended_actions" :key="i" x-small outlined>
                              {{ action }}
                            </v-chip>
            </div>
                          <span v-else class="text-gray-500 text-xs">-</span>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
            </div>
              <div v-else-if="loadingAudit" class="PluginDetail__placeholder">
                <the-icon icon="loading" class="mr-2" />
                Loading policy evaluations...
              </div>
              <div v-else class="PluginDetail__placeholder">
                <the-icon icon="shield" class="mr-2" />
                No policy evaluations available. Click "Evaluate Policies" to run evaluation.
              </div>
            </div>

            <!-- Enforcement Actions -->
            <div class="mb-6" v-if="policyEvaluations && policyEvaluations.enforcement_actions && policyEvaluations.enforcement_actions.length > 0">
              <h6 class="PluginDetail__subsection-heading">Enforcement Actions</h6>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Action</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Reason</th>
                      <th class="text-left">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="action in policyEvaluations.enforcement_actions" :key="action.id">
                      <td>{{ action.action_type }}</td>
                      <td>
                        <v-chip x-small :color="getEnforcementStatusColor(action.status)" dark>
                          {{ action.status }}
                        </v-chip>
                      </td>
                      <td class="text-xs">{{ action.reason }}</td>
                      <td>{{ formatDate(action.created_at) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>
        </div>
      </v-col>

      <v-col>
        <!-- Plugin Information Section - Always shown -->
        <div class="section-block mb-6">
          <h6 class="PluginDetail__section-heading">Plugin Information</h6>
          <div v-if="appDetail && appDetail.siteInfo && appDetail.siteInfo.plugin_info">
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Publisher Name</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.plugin_info.publisher_name }}
              </span>
            </div>
            <div class="d-flex mb-4" v-if="appDetail.siteInfo.domain">
              <span class="PluginDetail__label">Domain</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.domain }}
              </span>
            </div>
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Deploy Method</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.deployment_method }}
              </span>
            </div>
            <div class="d-flex mb-4" v-if="appDetail.siteInfo.github_path">
              <span class="PluginDetail__label">Repository</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.github_path }}
              </span>
            </div>
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Compilation Engine</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.compiler }}
              </span>
            </div>
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Turbo JS</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{
                  appDetail.siteInfo.turbojs_enabled === true
                    ? 'Enabled'
                    : 'Disabled'
                }}
              </span>
            </div>
            <div class="d-flex mb-4" v-if="appDetail.siteInfo.ssl_enabled !== undefined">
              <span class="PluginDetail__label">SSL Enabled</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.ssl_enabled ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="d-flex mb-4" v-if="appDetail.siteInfo.force_ssl_enabled !== undefined">
              <span class="PluginDetail__label">Force SSL</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.force_ssl_enabled ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="d-flex mb-4" v-if="appDetail.siteInfo.plugin_info.analysis_status">
              <span class="PluginDetail__label">Analysis Status</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.plugin_info.analysis_status }}
              </span>
            </div>
            <div class="d-flex mb-4" v-if="appDetail.forge_site_id">
              <span class="PluginDetail__label">Forge Site ID</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.forge_site_id }}
              </span>
            </div>
            <div class="d-flex mb-4" v-if="appDetail.forge_environment">
              <span class="PluginDetail__label">Forge Environment</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.forge_environment }}
              </span>
            </div>
          </div>
          <div v-else class="PluginDetail__placeholder">
            <the-icon icon="console" class="mr-2" />
            Plugin information not available - Forge site configuration may be incomplete
          </div>
        </div>

        <!-- Plugin Usage & Statistics Section - Combined -->
        <div class="section-block mb-6">
          <h6 class="PluginDetail__section-heading">Usage & Statistics</h6>
          
          <!-- Usage Metrics -->
          <div v-if="appDetail && appDetail.siteInfo" class="mb-6">
            <h6 class="PluginDetail__subsection-heading mb-4">Usage Metrics</h6>
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Bandwidth - Day</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.bandwidth_usage_day || 'No data' }}
              </span>
            </div>
            <div class="d-flex mb-4">
              <span class="PluginDetail__label">Bandwidth - Week</span>
              <span class="text-sm block tracking-wide text-gray-600">
                {{ appDetail.siteInfo.bandwidth_usage_week || 'No data' }}
              </span>
            </div>
            <div v-if="appDetail.siteInfo.forms_enabled">
              <div class="d-flex mb-4">
                <span class="PluginDetail__label">Forms</span>
                <span class="text-sm block tracking-wide text-gray-600">
                  {{ appDetail.siteInfo.forms_count }}
                </span>
              </div>
              <div class="d-flex mb-4">
                <span class="PluginDetail__label">Form Entries</span>
                <span class="text-sm block tracking-wide text-gray-600">
                  {{ appDetail.developer.forms_entries || 'N/A' }}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Statistics -->
          <div>
            <h6 class="PluginDetail__subsection-heading mb-4">Statistics</h6>
            <div v-if="loadingInstalls" class="PluginDetail__placeholder">
              <the-icon icon="loading" class="mr-2" />
              Loading installation statistics...
            </div>
            <div v-else-if="installsData" class="d-flex justify-center py-4 lg:pt-4 pt-8">
              <div class="mr-4 p-3 text-center">
                <div class="PluginDetail__statistics-label">Total Installs</div>
                <div class="PluginDetail__statistics-value">
                  {{ installsData.total_installations || installsData.active_installations || 0 }}
                </div>
              </div>
              <div class="mr-4 p-3 text-center" v-if="installsData.active_installations !== undefined">
                <div class="PluginDetail__statistics-label">Active Installs</div>
                <div class="PluginDetail__statistics-value">
                  {{ installsData.active_installations || 0 }}
                </div>
              </div>
              <div class="mr-4 p-3 text-center" v-if="appDetail && appDetail.developerData && appDetail.developerData.rating">
                <div class="PluginDetail__statistics-label">Rating</div>
                <div class="PluginDetail__statistics-value">
                  <v-rating
                    v-model="appDetail.developerData.rating"
                    readonly
                    background-color="red lighten-3"
                    color="red"
                    small
                    dense
                  ></v-rating>
                </div>
              </div>
            </div>
            <div v-else class="PluginDetail__placeholder">
              <the-icon icon="bar-chart" class="mr-2" />
              Installation statistics not available
            </div>
          </div>
        </div>

        <!-- Governance Policy Section - Always shown -->
        <div class="section-block mb-6">
          <h6 class="PluginDetail__section-heading">Governance Policy</h6>
          <div v-if="assignedPolicySet">
            <div class="d-flex mb-4">
              <div class="PluginDetail__policy-card px-4 mb-2">
                <div class="d-flex py-4 align-center">
                  <div class="policy-icon-button pa-2 mr-4">
                    <the-icon icon="document" class="policy-icon" />
                  </div>
                  {{ assignedPolicySet.name }}
                </div>
                <v-btn
                  class="v-btn policy-button"
                  @click="onViewPolicySet"
                  elevation="2"
                >
                  <v-icon dark left> mdi-eye </v-icon>
                  View
                </v-btn>
              </div>
            </div>
            <div v-if="policyEvaluations && policyEvaluations.summary" class="mt-4">
              <div class="d-flex flex-wrap gap-3">
                <div>
                  <span class="text-xs text-gray-500">Overall Status:</span>
                  <v-chip x-small :color="getEvaluationResultColor(policyEvaluations.summary.overall_status)" dark class="ml-1">
                    {{ policyEvaluations.summary.overall_status }}
                  </v-chip>
                </div>
                <div>
                  <span class="text-xs text-gray-500">Pass:</span>
                  <v-chip x-small color="success" dark class="ml-1">{{ policyEvaluations.summary.pass }}</v-chip>
                </div>
                <div>
                  <span class="text-xs text-gray-500">Warn:</span>
                  <v-chip x-small color="warning" dark class="ml-1">{{ policyEvaluations.summary.warn }}</v-chip>
                </div>
                <div>
                  <span class="text-xs text-gray-500">Fail:</span>
                  <v-chip x-small color="error" dark class="ml-1">{{ policyEvaluations.summary.fail }}</v-chip>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="loadingAudit" class="PluginDetail__placeholder">
            <the-icon icon="loading" class="mr-2" />
            Loading policy information...
          </div>
          <div v-else class="PluginDetail__placeholder">
            <the-icon icon="policy" class="mr-2" />
            Governance policy not assigned - please assign a policy for compliance
          </div>
        </div>

        <!-- Install Params Section - Always shown -->
        <div class="section-block mb-6 pr-0">
          <div class="d-flex justify-space-between">
            <h6 class="PluginDetail__section-heading">Install Params</h6>
            <v-btn v-if="schema.fields && schema.fields.length > 0" icon @click="toggleParamPreview">
              <v-icon v-if="inParamPreview">mdi-code-json</v-icon>
              <the-icon :icon="'eye-on'" v-else />
            </v-btn>
          </div>
          <div v-if="schema.fields && schema.fields.length > 0" class="install-params-section-block pr-6">
            <vue-form-generator
              v-if="inParamPreview"
              tag="div"
              :schema="schema"
              :model="param"
            ></vue-form-generator>
            <JSONPrint v-else :value="schema.fields" />
          </div>
          <div v-else class="PluginDetail__placeholder">
            <the-icon icon="settings" class="mr-2" />
            No installation parameters configured - plugin may not require configuration
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <!-- Remove Plugin Section - Always shown -->
        <div class="section-block mb-6">
          <div class="PluginDetail__section">
            <h6 class="PluginDetail__section-title">Remove Plugin</h6>
            <div class="PluginDetail__error-message _last">
              Note: This action is not reversable. <br />
              The plugin will be immediately removed and unrecoverable.
            </div>
            <div class="PluginDetail__controls">
              <v-btn
                class="PluginDetail__delete-btn"
                id="no-background-hover"
                @click="onRemovePlugin"
                color="transparent"
                depressed
                :disabled="!appDetail"
              >
                <v-icon>mdi-close-circle-outline</v-icon>
                <span class="ml-2">Remove {{ appDetail ? appDetail.name : 'Plugin' }}</span>
              </v-btn>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
    <div v-if="loading" class="plugin-detail-spinner">
      <FlamePreloader :compact="true" :size="100" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import FlamePreloader from '@/components/_Common/FlamePreloader'
import {
  MODAL_STATUS_CHANGE_CONFIRMATION,
  MODAL_ALERT,
} from '@/components/Modals'
import StatusChip from '@/components/_Common/StatusChip'
import JSONPrint from '@/components/_Common/JSONPrint'
import { safeJSONParse } from '@/utils/common'

const colorMaps = {
  success: 'PluginDetail__audit-green',
  warning: 'PluginDetail__audit-orange',
  error: 'PluginDetail__audit-red',
}

const defaultColor = {
  status: colorMaps['warning'],
  evalSafe: colorMaps['warning'],
  secureURL: colorMaps['success'],
  insecureURL: colorMaps['error'],
  externalNetworkRequests: colorMaps['success'],
  sslEnabled: colorMaps['success'],
  forceSSLEnabled: colorMaps['success'],
}

import marketplaceAPI from '@/services/marketplace'

export default {
  name: 'PluginDetail',
  components: {
    JSONPrint,
    FlamePreloader,
    StatusChip,
  },
  data() {
    return {
      loading: false,
      isOwned: false,
      colors: defaultColor,
      mainImageStyle: { backgroundColor: 'gray' },
      appDetail: null,
      schema: {
        fields: [],
      },
      param: {
        component: '',
      },
      inParamPreview: true,
      editingField: null, // Track which field is being edited
      editValue: '', // Current edit value (string for text fields)
      editArray: [], // Current edit value (array for categories/tags)
      assetGuidelines: null, // Asset guidelines from API
      // Predefined categories and tags
      predefinedCategories: [
        'analytics',
        'security',
        'productivity',
        'performance',
        'media',
        'monitoring',
        'ecommerce',
        'marketing',
        'seo',
        'social',
        'forms',
        'authentication',
        'payment',
        'integration',
        'utilities',
        'design',
        'content',
        'developer',
      ],
      predefinedTags: [
        'tracking',
        'encryption',
        'privacy',
        'images',
        'optimization',
        'crops',
        'metrics',
        'analytics',
        'monitoring',
        'performance',
        'speed',
        'caching',
        'cdn',
        'api',
        'webhook',
        'oauth',
        'payment',
        'stripe',
        'paypal',
        'email',
        'notification',
        'sms',
        'chat',
        'support',
        'helpdesk',
        'documentation',
        'search',
        'filter',
        'sort',
        'export',
        'import',
        'backup',
        'migration',
        'testing',
        'debugging',
        'logging',
      ],
      uploadingAsset: null, // Track which asset is uploading (icon, thumbnail, screenshots)
      uploadProgress: 0, // Upload progress percentage
      uploadErrors: [], // Upload validation errors
      tagSearchInput: '', // For custom tag input
      showApiKey: false, // Toggle to show/hide API key
      pluginVersions: [], // Plugin versions list
      latestAnalysisReport: null, // Latest analysis report from latest version
      policyEvaluations: null, // Policy evaluations for latest version
      loadingAudit: false, // Loading state for audit data
      refreshingAnalysis: false, // Loading state for analysis refresh
      evaluatingPolicies: false, // Loading state for policy evaluation
      assignedPolicySet: null, // Policy set assigned to this plugin (from evaluations)
      installsData: null, // Installation statistics from marketplace API
      loadingInstalls: false, // Loading state for installs data
      options: {
        inline: false,
        button: true,
        navbar: true,
        title: false,
        toolbar: false,
        tooltip: false,
        movable: false,
        zoomable: true,
        rotatable: false,
        scalable: true,
        transition: true,
        fullscreen: true,
        keyboard: true,
        url: 'data-source',
      },
    }
  },
  computed: {
    ...mapState({
      currentPublisherInfo: ({ publisher_infos }) => publisher_infos.current,
      user: ({ user }) => user.current,
      currentOrganisation: ({ organisations }) => organisations.current,
    }),
    isAdmin() {
      // Check organisation role (for publisher context)
      const org = this.currentOrganisation
      const orgRole = org?.role
      const isOrgAdmin = orgRole === 'admin' || orgRole === 'owner'
      
      // Also check user roles as fallback
      const user = this.user
      const hasRoles = user && user.roles
      const isUserAdmin = hasRoles && user.roles.admin === true
      
      return isOrgAdmin || isUserAdmin
    },
    auditDots() {
      return [
        {
          id: 'content',
          status: this.getContentStatus(),
          tooltip: this.getContentTooltip(),
        },
        {
          id: 'code',
          status: this.getCodeAnalysisStatus(),
          tooltip: this.getCodeAnalysisTooltip(),
        },
        {
          id: 'security',
          status: this.getNetworkSecurityStatus(),
          tooltip: this.getNetworkSecurityTooltip(),
        },
        {
          id: 'policies',
          status: this.getPolicyComplianceStatus(),
          tooltip: this.getPolicyComplianceTooltip(),
        },
      ]
    },
  },
  mounted() {
    this.getPluginDetail()
    this.loadAssetGuidelines()
  },
  methods: {
    /**
     * Map marketplace plugin to legacy appDetail format
     * 
     * API response structure includes:
     * - id, slug, name, description, short_description, long_description, status
     * - website, support_email, documentation_url
     * - categories, tags, featured, featured_at
     * - readme, icon_url, thumbnail_url, screenshots (array of URLs)
     * - entrypoint_url, forge_environment, forge_site_id, forge_api_configured, iframe_src
     * - sandbox_permissions: [] (array of permission strings)
     * - developer: { id, name, company, verified, user_id, website, email }
     * - created_at, updated_at, last_updated_at
     * - versions: [] (array of versions)
     * - site_info: { 
     *     kind, domain, compiler, forms_count, forms_enabled,
     *     plugin_info: { publisher_name, analysis_status, publisher_sdk_url },
     *     ssl_enabled, force_ssl_enabled, collaborators: [],
     *     last_fetched_at, turbojs_enabled, deployment_method,
     *     bandwidth_usage_day, bandwidth_usage_week
     *   }
     * - site_info_cached, site_info_fetched_at
     */
    mapPluginToAppDetail(plugin) {
      const developer = plugin.developer || {}
      
      // Map status from marketplace format to legacy format
      let legacyStatus = plugin.status
      if (plugin.status === 'active') legacyStatus = 'Ready for Sale'
      if (plugin.status === 'draft') legacyStatus = 'Draft'
      if (plugin.status === 'suspended') legacyStatus = 'Removed from Sale'
      
      // Extract screenshot URLs from screenshots array (can be objects with url property or just URLs)
      const screenshotUrls = (plugin.screenshots || []).map(s => 
        typeof s === 'string' ? s : s.url || s
      )
      
      return {
        id: plugin.id,
        name: plugin.name,
        slug: plugin.slug,
        url: plugin.entrypoint_url || plugin.iframe_src || '',
        // Developer information
        developer: {
          id: developer.id,
          name: developer.name || '',
          company: developer.company || '',
          verified: developer.verified || false,
          user_id: developer.user_id || null,
          website: developer.website || plugin.website || '',
          email: developer.email || plugin.support_email || '',
        },
        // Developer content (marketing fields)
        developerContent: {
          keyImage: plugin.thumbnail_url || plugin.icon_url || null,
          description: plugin.short_description || plugin.description || '',
          longDescription: plugin.long_description || plugin.description || '',
          shortName: plugin.slug || plugin.name || '',
          website: plugin.website || '',
          supportEmail: plugin.support_email || '',
          documentationUrl: plugin.documentation_url || '',
          categories: plugin.categories || [],
          tags: plugin.tags || [],
          featured: plugin.featured || false,
          featuredAt: plugin.featured_at || null,
          readme: plugin.readme || '',
          iconUrl: plugin.icon_url || null,
          thumbnailUrl: plugin.thumbnail_url || null,
          screenshots: screenshotUrls,
          privacyURL: plugin.privacy_url || plugin.privacy_policy_url || '',
          termsURL: plugin.terms_url || plugin.terms_of_service_url || '',
        },
        // Developer data (for status and install params)
        developerData: {
          id: plugin.id,
          status: legacyStatus,
          installsCount: plugin.installs_count || 0,
          rating: plugin.rating || 0,
          installParams: plugin.install_params ? JSON.stringify(plugin.install_params) : '[]',
        },
        // Site info - use complete site_info from API response if available
        siteInfo: plugin.site_info ? {
          // Use the complete site_info structure from the API
          kind: plugin.site_info.kind || 'Plugin',
          domain: plugin.site_info.domain || '',
          compiler: plugin.site_info.compiler || 'Unknown',
          forms_count: plugin.site_info.forms_count || 0,
          plugin_info: {
            publisher_name: plugin.site_info.plugin_info?.publisher_name || developer.name || developer.company || '',
            analysis_status: plugin.site_info.plugin_info?.analysis_status || null,
            publisher_sdk_url: plugin.site_info.plugin_info?.publisher_sdk_url || null,
            analysis_result: {
              EvalSafe: null, // Will be fetched from analysis reports endpoint when available
              ExternalNetworkRequests: {
                secureNetworkURLs: [], // Will be populated from analysis reports when available
                insecureNetworkURLs: [], // Will be populated from analysis reports when available
              },
            },
          },
          ssl_enabled: plugin.site_info.ssl_enabled || false,
          force_ssl_enabled: plugin.site_info.force_ssl_enabled || false,
          collaborators: plugin.site_info.collaborators || [],
          forms_enabled: plugin.site_info.forms_enabled || false,
          last_fetched_at: plugin.site_info.last_fetched_at || null,
          turbojs_enabled: plugin.site_info.turbojs_enabled || false,
          deployment_method: plugin.site_info.deployment_method || plugin.forge_environment || 'Unknown',
          bandwidth_usage_day: plugin.site_info.bandwidth_usage_day || null,
          bandwidth_usage_week: plugin.site_info.bandwidth_usage_week || null,
          // Legacy fields for compatibility
          github_path: plugin.github_path || plugin.repository_url || '',
        } : {
          // Fallback if site_info is not available
          plugin_info: {
            publisher_name: developer.name || developer.company || '',
            analysis_result: {
              EvalSafe: null,
              ExternalNetworkRequests: {
                secureNetworkURLs: [],
                insecureNetworkURLs: [],
              },
            },
          },
          deployment_method: plugin.forge_environment || 'Unknown',
          github_path: plugin.github_path || plugin.repository_url || '',
          compiler: 'Unknown',
          turbojs_enabled: false,
          bandwidth_usage_day: null,
          bandwidth_usage_week: null,
          forms_enabled: false,
          forms_count: 0,
        },
        // Additional fields for compatibility
        devMode: plugin.dev_mode || false,
        devURL: plugin.dev_url || plugin.development_url || '',
        forge_api_configured: plugin.forge_api_configured || false, // API key configuration status
        forge_site_api_key: plugin.forge_site_api_key || null, // API key (may not be in response for security)
        forge_environment: plugin.forge_environment || null,
        forge_site_id: plugin.forge_site_id || null,
        sandbox_permissions: plugin.sandbox_permissions || [],
        site_info_cached: plugin.site_info_cached || false,
        site_info_fetched_at: plugin.site_info_fetched_at || null,
        // Include full plugin data for reference
        ...plugin,
      }
    },
    
    // get app detail, called on page load
    // - get app detail common fields
    //  -- images
    //  -- color(Status color, eval safe color)
    //  -- Check if the current loggedin user is the owner
    async getPluginDetail() {
      this.loading = true
      try {
        const pluginSlug = this.$route.params.pluginId
        const plugin = await marketplaceAPI.getPlugin(pluginSlug)
        
        if (plugin) {
          // Map marketplace plugin to legacy appDetail format
          this.appDetail = this.mapPluginToAppDetail(plugin)
          
          // Set main image style (prefer thumbnail, then icon)
          if (this.appDetail.developerContent.thumbnailUrl) {
            this.mainImageStyle = {
              backgroundImage: `url('${this.appDetail.developerContent.thumbnailUrl}')`,
            }
          } else if (this.appDetail.developerContent.iconUrl) {
            this.mainImageStyle = {
              backgroundImage: `url('${this.appDetail.developerContent.iconUrl}')`,
            }
          } else if (this.appDetail.developerContent.keyImage) {
            this.mainImageStyle = {
              backgroundImage: `url('${this.appDetail.developerContent.keyImage}')`,
            }
          }
          
          // Parse install params for schema
          this.schema.fields = safeJSONParse(
            this.appDetail.developerData?.installParams || '[]'
          )

          // Audit data will be loaded separately via loadAuditData()
          // This keeps the initial load fast and allows for better error handling

          // Check if the current logged-in user is the owner
          // Compare developer.user_id with current user ID
          const developer = plugin.developer || {}
          if (this.user && developer.user_id) {
            this.isOwned = developer.user_id.toString() === this.user.id.toString()
          } else {
            this.isOwned = false
          }
          
          // Load audit data after plugin detail is loaded
          this.loadAuditData()
          
          // Load installation statistics asynchronously
          this.loadInstallsData()
        }
      } catch (error) {
        console.error('Error fetching plugin detail:', error)
        // Show error to user
        this.appDetail = null
      } finally {
        this.loading = false
      }
    },

    // Status Color
    getStatusColors() {
      const status = this.appDetail.developerData.status
      if (status === 'Ready for Sale') {
        return 'sale-ready'
      }
      if (status === 'Declined' || status === 'Removed from Sale') {
        return 'declined'
      }
      if (status === 'Need Review') {
        return 'need-review'
      }
    },

    // Audit Dots Status Calculation Methods
    getContentStatus() {
      if (!this.appDetail) {
        return 'unknown'
      }
      
      const content = this.appDetail.developerContent || {}
      const developer = this.appDetail.developer || {}
      
      // Required fields - check correct paths based on mapPluginToAppDetail mapping
      const hasName = this.appDetail.name && this.appDetail.name.trim() !== ''
      const hasDescription = content.description && content.description.trim() !== ''
      const hasIcon = content.iconUrl || content.icon
      const hasThumbnail = content.thumbnailUrl || content.keyImage
      const hasCategories = content.categories && content.categories.length > 0
      const hasDeveloperInfo = developer.name && developer.email
      
      // Recommended fields
      const hasLongDescription = content.longDescription && content.longDescription.trim() !== ''
      const hasScreenshots = content.screenshots && content.screenshots.length > 0
      
      const requiredFields = [hasName, hasDescription, hasIcon, hasThumbnail, hasCategories, hasDeveloperInfo]
      const requiredCount = requiredFields.filter(Boolean).length
      const requiredTotal = requiredFields.length
      
      if (requiredCount === requiredTotal) {
        return 'pass'
      } else if (requiredCount >= 4) {
        return 'warn'
      } else {
        return 'fail'
      }
    },
    
    getContentTooltip() {
      if (!this.appDetail) {
        return 'Content: No plugin data available'
      }
      
      const content = this.appDetail.developerContent || {}
      const developer = this.appDetail.developer || {}
      
      // Field labels for user-friendly display
      const fieldLabels = {
        name: 'Name',
        description: 'Description',
        icon: 'Icon',
        thumbnail: 'Thumbnail',
        categories: 'Categories',
        developerInfo: 'Developer Info',
      }
      
      // Check correct paths based on mapPluginToAppDetail mapping
      const checks = {
        name: this.appDetail.name && this.appDetail.name.trim() !== '',
        description: content.description && content.description.trim() !== '',
        icon: content.iconUrl || content.icon,
        thumbnail: content.thumbnailUrl || content.keyImage,
        categories: content.categories && content.categories.length > 0,
        developerInfo: developer.name && developer.email,
      }
      
      const passed = Object.values(checks).filter(Boolean).length
      const total = Object.keys(checks).length
      const status = this.getContentStatus()
      
      // Get missing fields with user-friendly labels
      const missingFields = Object.entries(checks)
        .filter(([_, value]) => !value)
        .map(([key]) => fieldLabels[key] || key)
      
      if (status === 'pass') {
        return `Content: All required fields complete (${passed}/${total})`
      } else if (status === 'warn') {
        if (missingFields.length > 0) {
          return `Content: Missing recommended fields - ${missingFields.join(', ')} (${passed}/${total})`
        }
        return `Content: Required fields complete, missing recommended fields (${passed}/${total})`
      } else if (status === 'fail') {
        if (missingFields.length > 0) {
          return `Content: Missing critical fields - ${missingFields.join(', ')} (${passed}/${total})`
        }
        return `Content: Missing critical fields (${passed}/${total})`
      }
      return 'Content: No plugin data available'
    },
    
    getCodeAnalysisStatus() {
      if (!this.pluginVersions || this.pluginVersions.length === 0) {
        return 'unknown'
      }
      
      if (!this.latestAnalysisReport || !this.latestAnalysisReport.summary) {
        return 'fail'
      }
      
      const evalSafe = this.latestAnalysisReport.summary.eval_safe
      const reportDate = this.latestAnalysisReport.generated_at
      
      if (evalSafe === null || evalSafe === undefined) {
        return 'unknown'
      }
      
      // Check if report is stale (> 30 days)
      const isStale = reportDate ? this.isReportStale(reportDate) : false
      
      if (evalSafe >= 10 && !isStale) {
        return 'pass'
      } else if (evalSafe >= 5 || (evalSafe >= 10 && isStale)) {
        return 'warn'
      } else {
        return 'fail'
      }
    },
    
    getCodeAnalysisTooltip() {
      if (!this.pluginVersions || this.pluginVersions.length === 0) {
        return 'Code Analysis: No versions uploaded'
      }
      
      if (!this.latestAnalysisReport || !this.latestAnalysisReport.summary) {
        return 'Code Analysis: No report available'
      }
      
      const evalSafe = this.latestAnalysisReport.summary.eval_safe
      const reportDate = this.latestAnalysisReport.generated_at
      const isStale = reportDate ? this.isReportStale(reportDate) : false
      const daysOld = reportDate ? this.getDaysSince(reportDate) : null
      
      const status = this.getCodeAnalysisStatus()
      
      if (status === 'pass') {
        const ageText = daysOld !== null ? `, Report: ${daysOld} day${daysOld !== 1 ? 's' : ''} old` : ''
        return `Code Analysis: Passing (Eval Safe: ${evalSafe}/10${ageText})`
      } else if (status === 'warn') {
        const staleText = isStale ? ', Report is stale' : ''
        return `Code Analysis: Warning (Eval Safe: ${evalSafe}/10${staleText})`
      } else if (status === 'fail') {
        return `Code Analysis: Failing (Eval Safe: ${evalSafe}/10)`
      }
      return 'Code Analysis: No report available'
    },
    
    getNetworkSecurityStatus() {
      if (!this.latestAnalysisReport || !this.latestAnalysisReport.summary) {
        return 'unknown'
      }
      
      const secureCount = this.latestAnalysisReport.summary.secure_network_urls_count || 0
      const insecureCount = this.latestAnalysisReport.summary.insecure_network_urls_count || 0
      const totalRequests = secureCount + insecureCount
      
      // No network requests is a pass (green)
      if (totalRequests === 0) {
        return 'pass'
      }
      
      // If there are network requests, green only if all are secure (no insecure URLs)
      if (insecureCount === 0) {
        return 'pass'
      } else if (insecureCount > 0 && secureCount > insecureCount) {
        return 'warn'
      } else {
        return 'fail'
      }
    },
    
    getNetworkSecurityTooltip() {
      if (!this.latestAnalysisReport || !this.latestAnalysisReport.summary) {
        return 'Network Security: No analysis data'
      }
      
      const secureCount = this.latestAnalysisReport.summary.secure_network_urls_count || 0
      const insecureCount = this.latestAnalysisReport.summary.insecure_network_urls_count || 0
      const totalRequests = secureCount + insecureCount
      
      // No network requests is a pass
      if (totalRequests === 0) {
        return 'Network Security: No network requests (pass)'
      }
      
      const status = this.getNetworkSecurityStatus()
      
      if (status === 'pass') {
        return `Network Security: All secure (${secureCount} HTTPS URL${secureCount !== 1 ? 's' : ''}, 0 insecure)`
      } else if (status === 'warn') {
        return `Network Security: Review needed (${secureCount} HTTPS, ${insecureCount} insecure URL${insecureCount !== 1 ? 's' : ''})`
      } else if (status === 'fail') {
        return `Network Security: Insecure URLs detected (${secureCount} HTTPS, ${insecureCount} insecure URL${insecureCount !== 1 ? 's' : ''})`
      }
      return 'Network Security: No network requests'
    },
    
    getPolicyComplianceStatus() {
      if (!this.policyEvaluations || !this.policyEvaluations.summary) {
        return 'unknown'
      }
      
      const overallStatus = this.policyEvaluations.summary.overall_status?.toLowerCase()
      const failCount = this.policyEvaluations.summary.fail || 0
      
      if (overallStatus === 'pass' && failCount === 0) {
        return 'pass'
      } else if (overallStatus === 'warn' || (failCount === 0 && this.policyEvaluations.summary.warn > 0)) {
        return 'warn'
      } else if (overallStatus === 'fail' || failCount > 0) {
        return 'fail'
      }
      
      return 'unknown'
    },
    
    getPolicyComplianceTooltip() {
      if (!this.policyEvaluations || !this.policyEvaluations.summary) {
        return 'Policy Compliance: No evaluations available'
      }
      
      const summary = this.policyEvaluations.summary
      const passCount = summary.pass || 0
      const warnCount = summary.warn || 0
      const failCount = summary.fail || 0
      const overallStatus = summary.overall_status || 'unknown'
      
      const status = this.getPolicyComplianceStatus()
      
      if (status === 'pass') {
        return `Policy Compliance: Passing (${passCount} pass, ${warnCount} warn, ${failCount} fail)`
      } else if (status === 'warn') {
        return `Policy Compliance: Warning (${passCount} pass, ${warnCount} warn, ${failCount} fail)`
      } else if (status === 'fail') {
        return `Policy Compliance: Failing (${passCount} pass, ${warnCount} warn, ${failCount} fail)`
      }
      return 'Policy Compliance: No evaluations available'
    },
    
    getAuditDotClass(status) {
      switch (status) {
        case 'pass':
          return 'PluginDetail__audit-green'
        case 'warn':
          return 'PluginDetail__audit-orange'
        case 'fail':
          return 'PluginDetail__audit-red'
        case 'unknown':
        default:
          return 'PluginDetail__audit-grey'
      }
    },
    
    isReportStale(dateString) {
      if (!dateString) return false
      const reportDate = new Date(dateString)
      const daysSince = this.getDaysSince(dateString)
      return daysSince > 30
    },
    
    getDaysSince(dateString) {
      if (!dateString) return null
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      return Math.floor(diffTime / (1000 * 60 * 60 * 24))
    },

    onUpdateStatus(params) {
      this.$store.commit('application/openModal', {
        component: MODAL_STATUS_CHANGE_CONFIRMATION,
        props: {
          title: 'Are you sure you want to change status?',
          text: 'Status change affect its operation and display on other sites.',
          showCancelBtn: true,
          actionBtnLabel: 'Confirm',
          action: () => {
            this.updatePluginStatus(params)
          },
        },
      })
    },
    // Listener of Status Chip component update status
    async updatePluginStatus(params) {
      const { status } = params
      this.loading = true
      // Update status on Forge Ignite
      try {
        await Parse.Cloud.run('updateDeveloperAppData', {
          appDataId: this.appDetail.developerData.id,
          status,
          parseServerSiteId: this.currentPublisherInfo.parse_server_site_id,
        })
      } catch (error) {
        console.error('Error while Updating DeveloperAppData', error)
        this.loading = false
        return
      }
      this.appDetail.developerData.status = status
      this.loading = false
    },

    toggleParamPreview() {
      this.inParamPreview = !this.inParamPreview
    },

    async onRemovePlugin() {
      const pluginSlug = this.$route.params.pluginId
      
      // Get developer ID from plugin or current user
      let developerId = null
      if (this.appDetail && this.appDetail.developer && this.appDetail.developer.id) {
        developerId = this.appDetail.developer.id
      } else {
        // Try to find developer by current user email
        try {
          const { findDeveloperByEmail } = await import('@/utils/parse')
          const developer = await findDeveloperByEmail(
            this.currentPublisherInfo?.parse_server_site_id,
            this.user?.email
          )
          if (developer && developer.id) {
            developerId = developer.id
          }
        } catch (error) {
          console.warn('Could not find developer:', error)
        }
      }
      
      if (!developerId) {
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Remove Failed',
            text: 'Developer ID not found. Cannot remove plugin.',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
        return
      }
      
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Remove Plugin',
          text: `You are going to remove ${this.appDetail ? this.appDetail.name : 'this plugin'}. Are you sure?`,
          actionBtnLabel: 'Remove',
          showCancelBtn: true,
          action: async () => {
            try {
              this.loading = true
              await marketplaceAPI.deletePlugin(pluginSlug, developerId)
              // Success - navigate to plugin list
              this.$router.push({ name: 'plugin_list' })
            } catch (error) {
              console.error('Error removing plugin:', error)
              this.loading = false
              this.$store.commit('application/openModal', {
                component: MODAL_ALERT,
                props: {
                  title: 'Remove Failed',
                  text: error.message || 'Failed to remove plugin. Please try again.',
                  actionBtnLabel: 'OK',
                  showCancelBtn: false,
                },
              })
            }
          },
        },
      })
    },

    onViewPolicySet() {
      if (this.assignedPolicySet) {
        this.$router.push({
          name: 'publisher_policy_detail',
          params: { policyId: this.assignedPolicySet.id },
        })
      }
    },

    // ============================================
    // Asset Guidelines
    // ============================================

    async loadAssetGuidelines() {
      try {
        const response = await marketplaceAPI.getAssetGuidelines()
        this.assetGuidelines = response.asset_guidelines || response
      } catch (error) {
        console.warn('Failed to load asset guidelines:', error)
        // Continue without guidelines - defaults will be used
      }
    },

    // ============================================
    // Marketing Fields Editing
    // ============================================

    startEdit(fieldName) {
      if (!this.appDetail || !this.appDetail.developerContent) return
      
      // Get current value based on field name
      if (fieldName === 'categories' || fieldName === 'tags') {
        // For array fields, use editArray
        this.editArray = [...(this.appDetail.developerContent[fieldName] || [])]
        this.editingField = fieldName
        this.editValue = '' // Clear string value
      } else {
        // For string fields, use editValue
        let value = ''
        if (fieldName === 'description') {
          value = this.appDetail.developerContent.description || ''
        } else if (fieldName === 'longDescription') {
          value = this.appDetail.developerContent.longDescription || ''
        } else if (fieldName === 'website') {
          value = this.appDetail.developerContent.website || ''
        } else if (fieldName === 'supportEmail') {
          value = this.appDetail.developerContent.supportEmail || ''
        } else if (fieldName === 'documentationUrl') {
          value = this.appDetail.developerContent.documentationUrl || ''
      } else if (fieldName === 'readme') {
        value = this.appDetail.developerContent.readme || ''
      } else if (fieldName === 'forgeSiteApiKey') {
        value = this.appDetail.forge_site_api_key || ''
      }
      
      this.editingField = fieldName
      this.editValue = value
      this.editArray = [] // Clear array value
      }
    },

    cancelEdit() {
      this.editingField = null
      this.editValue = ''
      this.editArray = []
      this.tagSearchInput = ''
      this.showApiKey = false
      this.uploadErrors = []
    },

    addCustomTag() {
      if (this.tagSearchInput && this.tagSearchInput.trim() && !this.editArray.includes(this.tagSearchInput.trim())) {
        this.editArray.push(this.tagSearchInput.trim())
        this.tagSearchInput = ''
      }
    },

    async saveField(fieldName) {
      if (!this.appDetail || !this.appDetail.developer) return
      
      this.loading = true
      this.uploadErrors = []
      
      try {
        const pluginSlug = this.$route.params.pluginId
        const developerId = this.appDetail.developer.id
        
        // Prepare update data based on field name
        const updateData = {}
        
        if (fieldName === 'description') {
          updateData.short_description = this.editValue
        } else if (fieldName === 'longDescription') {
          updateData.long_description = this.editValue
        } else if (fieldName === 'website') {
          updateData.website = this.editValue
        } else if (fieldName === 'supportEmail') {
          updateData.support_email = this.editValue
        } else if (fieldName === 'documentationUrl') {
          updateData.documentation_url = this.editValue
        } else if (fieldName === 'categories') {
          updateData.categories = this.editArray
        } else if (fieldName === 'tags') {
          updateData.tags = this.editArray
        } else if (fieldName === 'readme') {
          updateData.readme = this.editValue
        } else if (fieldName === 'forgeSiteApiKey') {
          updateData.forge_site_api_key = this.editValue
        }
        
        // Update via API
        // Note: For forge_site_api_key, we might need to use admin credentials
        // For now, using developerId - API should handle admin permissions
        const updated = await marketplaceAPI.updatePlugin(pluginSlug, developerId, updateData)
        
        // Refresh plugin data
        await this.getPluginDetail()
        
        this.editingField = null
        this.editValue = ''
        this.editArray = []
      } catch (error) {
        console.error('Error updating field:', error)
        this.uploadErrors = [error.message || 'Failed to update field']
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Update Failed',
            text: error.message || 'Failed to update field. Please try again.',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } finally {
        this.loading = false
      }
    },

    // ============================================
    // Asset Upload
    // ============================================

    async handleIconUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      if (!this.appDetail || !this.appDetail.developer) {
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Upload Failed',
            text: 'Developer information not available',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
        return
      }

      // Client-side validation
      if (this.assetGuidelines && this.assetGuidelines.icon) {
        const errors = this.validateFile(file, this.assetGuidelines.icon)
        if (errors.length > 0) {
          this.uploadErrors = errors
          return
        }
      }

      this.uploadingAsset = 'icon'
      this.uploadProgress = 0
      this.uploadErrors = []

      try {
        const pluginSlug = this.$route.params.pluginId
        const developerId = this.appDetail.developer.id
        
        const result = await marketplaceAPI.uploadIcon(pluginSlug, file, developerId)
        
        // Refresh plugin data
        await this.getPluginDetail()
        
        this.uploadingAsset = null
      } catch (error) {
        console.error('Error uploading icon:', error)
        this.uploadErrors = error.data?.errors || [error.message || 'Upload failed']
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Upload Failed',
            text: (error.data?.errors || [error.message || 'Upload failed']).join(', '),
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } finally {
        this.uploadingAsset = null
        // Reset file input
        event.target.value = ''
      }
    },

    async handleThumbnailUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      if (!this.appDetail || !this.appDetail.developer) {
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Upload Failed',
            text: 'Developer information not available',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
        return
      }

      // Client-side validation
      if (this.assetGuidelines && this.assetGuidelines.thumbnail) {
        const errors = this.validateFile(file, this.assetGuidelines.thumbnail)
        if (errors.length > 0) {
          this.uploadErrors = errors
          return
        }
      }

      this.uploadingAsset = 'thumbnail'
      this.uploadProgress = 0
      this.uploadErrors = []

      try {
        const pluginSlug = this.$route.params.pluginId
        const developerId = this.appDetail.developer.id
        
        const result = await marketplaceAPI.uploadThumbnail(pluginSlug, file, developerId)
        
        // Refresh plugin data
        await this.getPluginDetail()
        
        this.uploadingAsset = null
      } catch (error) {
        console.error('Error uploading thumbnail:', error)
        this.uploadErrors = error.data?.errors || [error.message || 'Upload failed']
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Upload Failed',
            text: (error.data?.errors || [error.message || 'Upload failed']).join(', '),
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } finally {
        this.uploadingAsset = null
        event.target.value = ''
      }
    },

    async handleScreenshotsUpload(event) {
      const files = event.target.files
      if (!files || files.length === 0) return
      
      if (!this.appDetail || !this.appDetail.developer) {
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Upload Failed',
            text: 'Developer information not available',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
        return
      }

      // Client-side validation
      if (this.assetGuidelines && this.assetGuidelines.screenshots) {
        const errors = []
        Array.from(files).forEach(file => {
          const fileErrors = this.validateFile(file, this.assetGuidelines.screenshots)
          errors.push(...fileErrors)
        })
        
        // Check max count
        const currentCount = (this.appDetail.developerContent.screenshots || []).length
        const maxCount = this.assetGuidelines.screenshots.max_count || 10
        if (currentCount + files.length > maxCount) {
          errors.push(`Cannot upload ${files.length} screenshots. Maximum ${maxCount} allowed (currently ${currentCount}).`)
        }
        
        if (errors.length > 0) {
          this.uploadErrors = errors
          return
        }
      }

      this.uploadingAsset = 'screenshots'
      this.uploadProgress = 0
      this.uploadErrors = []

      try {
        const pluginSlug = this.$route.params.pluginId
        const developerId = this.appDetail.developer.id
        
        const result = await marketplaceAPI.uploadScreenshots(pluginSlug, files, developerId)
        
        // Refresh plugin data
        await this.getPluginDetail()
        
        this.uploadingAsset = null
      } catch (error) {
        console.error('Error uploading screenshots:', error)
        this.uploadErrors = error.data?.errors || [error.message || 'Upload failed']
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Upload Failed',
            text: (error.data?.errors || [error.message || 'Upload failed']).join(', '),
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } finally {
        this.uploadingAsset = null
        event.target.value = ''
      }
    },

    async handleDeleteScreenshot(screenshotIndex) {
      if (!this.appDetail || !this.appDetail.developer) return
      
      const pluginSlug = this.$route.params.pluginId
      
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Delete Screenshot',
          text: 'Are you sure you want to delete this screenshot?',
          actionBtnLabel: 'Delete',
          showCancelBtn: true,
          action: async () => {
            try {
              this.loading = true
              
              // Get screenshot ID from API (screenshots list includes IDs)
              const screenshots = await marketplaceAPI.listScreenshots(pluginSlug)
              
              if (!screenshots || screenshots.length <= screenshotIndex) {
                throw new Error('Screenshot not found')
              }
              
              const screenshot = screenshots[screenshotIndex]
              const screenshotId = screenshot.id
              const developerId = this.appDetail.developer.id
              
              await marketplaceAPI.deleteScreenshot(pluginSlug, screenshotId, developerId)
              
              // Refresh plugin data
              await this.getPluginDetail()
            } catch (error) {
              console.error('Error deleting screenshot:', error)
              this.$store.commit('application/openModal', {
                component: MODAL_ALERT,
                props: {
                  title: 'Delete Failed',
                  text: error.message || 'Failed to delete screenshot',
                  actionBtnLabel: 'OK',
                  showCancelBtn: false,
                },
              })
            } finally {
              this.loading = false
            }
          },
        },
      })
    },

    validateFile(file, guidelines) {
      const errors = []

      // File size check
      if (file.size > guidelines.max_file_size) {
        errors.push(
          `File size (${(file.size / 1024 / 1024).toFixed(2)} MB) exceeds maximum (${guidelines.max_file_size_mb} MB)`
        )
      }

      // Content type check
      if (!guidelines.allowed_content_types.includes(file.type)) {
        errors.push(
          `Content type (${file.type}) not allowed. Allowed types: ${guidelines.allowed_content_types.join(', ')}`
        )
      }

      return errors
    },

    // ============================================
    // Audit Data Loading & Helpers
    // ============================================

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    getVersionStatusColor(status) {
      const statusColors = {
        draft: 'grey',
        submitted: 'orange',
        approved: 'blue',
        published: 'green',
        rejected: 'red',
        yanked: 'red',
        suspended: 'red',
      }
      return statusColors[status] || 'grey'
    },

    getRiskLevelColor(riskLevel) {
      const riskColors = {
        critical: 'red',
        high: 'orange',
        medium: 'yellow',
        low: 'green',
      }
      return riskColors[riskLevel] || 'grey'
    },

    getEvaluationResultColor(result) {
      const resultColors = {
        pass: 'green',
        warn: 'orange',
        fail: 'red',
      }
      return resultColors[result] || 'grey'
    },

    getEnforcementStatusColor(status) {
      const statusColors = {
        proposed: 'orange',
        approved: 'blue',
        executed: 'red',
        rolled_back: 'grey',
      }
      return statusColors[status] || 'grey'
    },

    async loadAuditData() {
      if (!this.appDetail) return
      
      this.loadingAudit = true
      try {
        const pluginSlug = this.$route.params.pluginId
        
        // Fetch plugin versions
        const versions = await marketplaceAPI.getPluginVersions(pluginSlug)
        this.pluginVersions = versions || []
        
        if (this.pluginVersions.length > 0) {
          // Get the latest version (assuming versions are ordered by created_at desc)
          const latestVersion = this.pluginVersions[0]
          
          // Fetch analysis reports for latest version
          try {
            const analysisResponse = await marketplaceAPI.getVersionAnalysisReports(latestVersion.id, 'static')
            if (analysisResponse && analysisResponse.reports && analysisResponse.reports.length > 0) {
              // Get the most recent report (first in array, ordered by most recent first)
              this.latestAnalysisReport = analysisResponse.reports[0]
              
              // Update eval safe color based on latest report
              const evalSafe = this.latestAnalysisReport.summary?.eval_safe
              if (evalSafe !== null && evalSafe !== undefined) {
                let color = { ...defaultColor }
                const EvalSafe_Approve_Min = 10
                const EvalSafe_Warning_FailBelow = 5
                if (evalSafe >= EvalSafe_Approve_Min) {
                  color['evalSafe'] = colorMaps['success']
                } else if (evalSafe < EvalSafe_Warning_FailBelow) {
                  color['evalSafe'] = colorMaps['error']
                }
                this.colors = color
              }
            }
          } catch (error) {
            console.warn('Failed to fetch analysis reports:', error)
          }
          
          // Fetch latest policy evaluations for latest version (read-only, doesn't trigger new evaluation)
          try {
            const policyResponse = await marketplaceAPI.getVersionLatestPolicyEvaluations(latestVersion.id)
            this.policyEvaluations = policyResponse
            
            // Extract policy set information from evaluations
            // Use the first policy set that has evaluations
            if (policyResponse.evaluations && policyResponse.evaluations.length > 0) {
              const firstEvaluation = policyResponse.evaluations[0]
              if (firstEvaluation.policy_set_id) {
                try {
                  const policySet = await marketplaceAPI.getPolicySet(firstEvaluation.policy_set_id)
                  this.assignedPolicySet = {
                    id: policySet.id,
                    name: policySet.name || 'Unnamed Policy Set',
                  }
                } catch (err) {
                  console.warn('Failed to fetch policy set details:', err)
                }
              }
            }
          } catch (error) {
            console.warn('Failed to fetch policy evaluations:', error)
          }
        }
      } catch (error) {
        console.error('Error loading audit data:', error)
      } finally {
        this.loadingAudit = false
      }
    },

    async refreshAnalysisReport() {
      if (!this.pluginVersions || this.pluginVersions.length === 0) {
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'No Versions',
            text: 'No plugin versions found. Cannot refresh analysis.',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
        return
      }

      // Get the latest version
      const latestVersion = this.pluginVersions[0]
      
      this.refreshingAnalysis = true
      try {
        // Trigger analysis refresh
        const response = await marketplaceAPI.refreshVersionAnalysis(latestVersion.id)
        
        // Show success message
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Analysis Refresh Started',
            text: 'Analysis refresh has been queued. The report will be available shortly. Refreshing data...',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
            action: () => {
              // Reload audit data after a short delay to allow the job to process
              setTimeout(() => {
                this.loadAuditData()
              }, 2000)
            },
          },
        })
      } catch (error) {
        console.error('Error refreshing analysis:', error)
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Refresh Failed',
            text: error.message || 'Failed to refresh analysis report. Please try again.',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } finally {
        this.refreshingAnalysis = false
      }
    },

    async triggerPolicyEvaluation() {
      if (!this.pluginVersions || this.pluginVersions.length === 0) {
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'No Versions',
            text: 'No plugin versions found. Cannot evaluate policies.',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
        return
      }

      // Get the latest version
      const latestVersion = this.pluginVersions[0]
      
      this.evaluatingPolicies = true
      try {
        // Trigger policy evaluation
        const response = await marketplaceAPI.evaluateVersionPolicies(latestVersion.id)
        
        // Update policy evaluations with the response
        this.policyEvaluations = response
        
        // Show success message
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Policy Evaluation Complete',
            text: `Policy evaluation completed. ${response.evaluations?.length || 0} policy set(s) evaluated.`,
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } catch (error) {
        console.error('Error evaluating policies:', error)
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Evaluation Failed',
            text: error.message || 'Failed to evaluate policies. Please try again.',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } finally {
        this.evaluatingPolicies = false
      }
    },

    // ============================================
    // Installation Statistics
    // ============================================

    async loadInstallsData() {
      if (!this.appDetail || !this.appDetail.id) return
      
      this.loadingInstalls = true
      try {
        const pluginId = this.appDetail.id
        
        // Fetch installations for this plugin
        const installations = await marketplaceAPI.listInstallations({
          plugin_id: pluginId,
          status: 'active',
        })
        
        // Count active installations
        const activeInstallations = installations.filter(inst => inst.status === 'active').length
        
        // Also try to get analytics data for more detailed stats
        try {
          const analytics = await marketplaceAPI.getInstallationAnalytics({
            plugin_id: pluginId,
          })
          
          // Use analytics data if available, otherwise use installation count
          this.installsData = {
            total_installations: analytics.total_installations || installations.length,
            active_installations: analytics.active_installations || activeInstallations,
            disabled_installations: analytics.disabled_installations || installations.filter(inst => inst.status === 'disabled').length,
            quarantined_installations: analytics.quarantined_installations || installations.filter(inst => inst.status === 'quarantined').length,
          }
        } catch (analyticsError) {
          // Fallback to simple count if analytics fails
          console.warn('Could not fetch installation analytics, using simple count:', analyticsError)
          this.installsData = {
            total_installations: installations.length,
            active_installations: activeInstallations,
            disabled_installations: installations.filter(inst => inst.status === 'disabled').length,
            quarantined_installations: installations.filter(inst => inst.status === 'quarantined').length,
          }
        }
      } catch (error) {
        console.error('Error loading installation statistics:', error)
        // Don't show error to user - just leave installsData as null
        this.installsData = null
      } finally {
        this.loadingInstalls = false
      }
    },
  },
  watch: {},
}
</script>
<style>
.breadcrumb-link {
  text-decoration: none;
  font-size: 15px;
}
.breadcrumb-separator {
  margin-left: 5px;
  margin-right: 5px;
}
</style>
<style lang="scss" scoped>
.section-block {
  border: 1px solid $N5;
  background: $N4;
  border-radius: 4px;
  padding: 24px;
}
$style: PluginDetail;
.#{$style} {
  &__logo {
    width: 150px;
    height: auto;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
    border-radius: 6px;
  }
  &__app-name {
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    color: white;
    margin-bottom: 9px;
  }
  &__link {
    color: $N2;
    fill: $N2;
    text-decoration: none;
    cursor: pointer;
  }
  &__status-audit {
    width: 180px;
    text-align: center;
  }

  &__audit-wrapper {
    display: inline-flex;
    margin-top: 20px;
  }

  &__audit-green {
    background: $G1;
  }
  &__audit-red {
    background: $R1;
  }
  &__audit-blue {
    background: $B1;
  }
  &__audit-orange {
    background: $Y1;
  }
  &__audit-grey {
    background: $N3;
    opacity: 0.6;
  }

  &__section-heading {
    color: white;
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 24px;
    font-weight: normal;
  }
  
  &__subsection-heading {
    color: white;
    font-size: 14px;
    line-height: 18px;
    margin-bottom: 16px;
    margin-top: 24px;
    font-weight: 500;
    
    &:first-child {
      margin-top: 0;
    }
  }
  &__label {
    width: 150px;
    flex-shrink: 0;
    color: $N28;
    font-size: 14px;
  }

  &__screenshot-wrapper {
    width: 40%;
    margin-left: 24px;
    flex-shrink: 0;
  }

  &__statistics-value {
    font-size: 20px;
    font-weight: bold;
  }

  &__statistics-label {
    font-size: 14px;
    color: $N2;
    margin-bottom: 8px;
  }

  &__section-title {
    color: $white;
    font-weight: 500;
    font-size: $H16;
    margin-bottom: 14px;
    &._margin-more {
      margin-bottom: 28px;
    }
  }

  &__error-message {
    background: rgba($R1, 0.3);
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 26px;
    &._last {
      color: $N2;
    }
  }
  &__delete-btn {
    &.v-btn {
      color: $R1;
    }
  }
  &__policy-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: $N7;
    border-radius: 8px;
    .policy-button {
      color: white;
      background: $N5;
    }
    .policy-icon-button {
      background: $N5;
      height: 40px;
      border-radius: 6px;
    }
    .policy-icon {
      color: $B1;
      fill: $B1;
    }
  }

  &__placeholder {
    display: flex;
    align-items: center;
    color: $N2;
    font-size: 14px;
    font-style: italic;
    padding: 8px 12px;
    background: rgba($N2, 0.1);
    border: 1px dashed rgba($N2, 0.3);
    border-radius: 4px;
    margin: 4px 0;
    
    .the-icon {
      color: $N2;
      fill: $N2;
      opacity: 0.7;
    }
  }
}

.plugin-detail-spinner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(100, 100, 100, 0.2);
  height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  justify-content: center;
}
.install-params-section-block {
  max-height: 300px;
  overflow-y: auto;
}

.methods {
  margin-bottom: 1em;
  flex-wrap: wrap;
  & > * {
    margin-right: 0.75rem;
  }
}
.viewer {
  height: 100%;

  .image-wrapper {
    display: inline-block;
    width: calc(33% - 20px);
    height: 200px;
    overflow: hidden;
    position: relative;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

.position-absolute {
  position: absolute;
}

.position-relative {
  position: relative;
}

.gap-2 {
  gap: 8px;
}

.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.PluginDetail__editable-row {
  position: relative;
  
  .PluginDetail__edit-btn {
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  &:hover .PluginDetail__edit-btn {
    opacity: 1;
  }
}
</style>
